/**
 * StagingDetail — review a single staged import: shows the ORIGINAL file next
 * to the extracted/mapped data (so the user can validate 100% before it ever
 * touches a real ERP table), then walks Confirm -> Submit -> Done, mirroring
 * the "document-import-flow" wireframe (Validate + Connect DB + Match are
 * combined into one side-by-side Review screen here since the master-data
 * lookups are instant and interactive, not a separate wait step).
 *
 * DocType-specific mapping:
 *  - purchase_order: supplier search + per-line Item Master search (full grid:
 *    description/HSN/qty/uom/rate/GST%/total).
 *  - purchase_invoice / sales_invoice: reuses InvoiceMapping as-is (it already
 *    computes a ready-to-post `savePayload` with GL-mapped Dr/Cr lines).
 *  - pan_card / gst_certificate: reuses KycMapping (writes the ID field onto a
 *    matched Customer/Supplier).
 *  - anything else: read-only preview — posting isn't wired up for that doc
 *    type yet, so it stays Pending.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Package, CheckCircle2, Loader2, AlertTriangle, Sparkles } from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API = API_BASE;
import InvoiceMapping from './InvoiceMapping';
import KycMapping from './KycMapping';
import JournalMapping from './JournalMapping';

const money = v => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const INVOICE_TYPES = ['purchase_invoice', 'sales_invoice', 'sales_return'];
const KYC_TYPES = ['pan_card', 'gst_certificate'];
const STEPS = ['Review & Match', 'Confirm', 'Done'];

function Stepper({ step, border, text, textMut }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div data-testid={`staging-step-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: i === step ? '#8b5cf620' : 'transparent' }}>
            {i < step ? <CheckCircle2 size={12} color="#22c55e" /> : <div style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${i === step ? '#8b5cf6' : textMut}` }} />}
            <span style={{ fontSize: 11, fontWeight: i === step ? 700 : 500, color: i === step ? '#8b5cf6' : i < step ? text : textMut }}>{i + 1}. {label}</span>
          </div>
          {i < STEPS.length - 1 && <div style={{ width: 20, height: 1, background: border }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function LineItemsGrid({ lineItems, taxLines, border, isDark, text, textMut }) {
  const cols = [
    { key: 'description', label: 'Product / Description', align: 'left', flex: 3 },
    { key: 'hsn', label: 'HSN', align: 'center', flex: 1 },
    { key: 'qty', label: 'Qty', align: 'right', flex: 1, fmt: v => v || '—' },
    { key: 'uom', label: 'UOM', align: 'center', flex: 1 },
    { key: 'rate', label: 'Rate', align: 'right', flex: 1, fmt: v => v ? money(v) : '—' },
    { key: 'gstPct', label: 'GST %', align: 'right', flex: 1, fmt: v => v ? `${v}%` : '—' },
    { key: 'amount', label: 'Total Value', align: 'right', flex: 1.4, fmt: money },
  ];
  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', padding: '8px 12px', background: isDark ? '#1a1a1a' : '#f8fafc', borderBottom: `1px solid ${border}` }}>
        {cols.map(c => <div key={c.key} style={{ flex: c.flex, fontSize: 9, fontWeight: 800, color: textMut, textTransform: 'uppercase', textAlign: c.align }}>{c.label}</div>)}
      </div>
      {(lineItems || []).map((li, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 12px', borderTop: i > 0 ? `1px solid ${border}` : 'none', gap: 4 }}>
          {cols.map(c => (
            <div key={c.key} style={{ flex: c.flex, fontSize: 11, color: c.key === 'description' ? text : textMut, textAlign: c.align, fontFamily: c.key === 'description' || c.key === 'hsn' || c.key === 'uom' ? 'inherit' : 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: c.key === 'amount' ? 700 : 400 }}>
              {c.fmt ? c.fmt(li[c.key]) : (li[c.key] || <span style={{ color: textMut }}>—</span>)}
            </div>
          ))}
        </div>
      ))}
      {(!lineItems || lineItems.length === 0) && <div style={{ padding: 16, fontSize: 11, color: textMut, textAlign: 'center' }}>No product line items were extracted from this document.</div>}
      {taxLines && taxLines.length > 0 && (
        <>
          <div style={{ padding: '7px 12px', fontSize: 10, fontWeight: 800, color: textMut, background: isDark ? '#1a1a1a' : '#f8fafc', borderTop: `1px solid ${border}` }}>TAX & OTHER CHARGES ({taxLines.length})</div>
          {taxLines.map((t, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 12px', fontSize: 11, borderTop: `1px solid ${border}` }}>
              <span style={{ color: text }}>{t.description}</span>
              <span style={{ color: '#f59e0b', fontFamily: 'monospace', fontWeight: 700 }}>{money(t.amount)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function POItemMapping({ extracted, onChange, isDark, border, bgCard, text, textMut, isGRN }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState(extracted.partyName || '');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [poNo, setPoNo] = useState(extracted.poNo || '');
  const lineItems = extracted.lineItems || [];
  const [itemSearch, setItemSearch] = useState(lineItems.map(li => li.description || ''));
  const [itemResults, setItemResults] = useState(lineItems.map(() => []));
  const [resolved, setResolved] = useState(lineItems.map(li => li.itemCode ? li : null));

  useEffect(() => {
    fetch(`${API}/api/supplier-master`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => setSuppliers(d.data || [])).catch(() => {});
  }, []);

  const filtered = suppliers.filter(s => s.SupplierName?.toLowerCase().includes(supplierSearch.toLowerCase())).slice(0, 30);

  useEffect(() => {
    onChange({ partyId: selectedSupplier?.SupplierId || '', partyName: selectedSupplier?.SupplierName || supplierSearch, poNo, items: lineItems.map((li, i) => ({ ...li, itemCode: resolved[i]?.Itemcode || li.itemCode })), valid: !!selectedSupplier && lineItems.length > 0 && resolved.every(r => r) });
  }, [selectedSupplier, resolved, poNo]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = (i, q) => {
    setItemSearch(s => { const a = [...s]; a[i] = q; return a; });
    setResolved(r => { const a = [...r]; a[i] = null; return a; });
    if (q.trim().length < 2) return;
    fetch(`${API}/api/document-hub/items?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => setItemResults(res => { const a = [...res]; a[i] = d.data || []; return a; })).catch(() => {});
  };

  const gridCols = [
    { key: 'description', label: 'Product / Description', flex: 2.4 },
    { key: 'hsn', label: 'HSN', flex: 0.9 },
    { key: 'qty', label: 'Qty', flex: 0.7 },
    { key: 'uom', label: 'UOM', flex: 0.7 },
    { key: 'rate', label: 'Rate', flex: 0.9 },
    { key: 'gstPct', label: 'GST %', flex: 0.7 },
    { key: 'amount', label: 'Total Value', flex: 1 },
  ];

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>SUPPLIER</div>
        <div style={{ position: 'relative' }}>
          <input data-testid="staging-po-supplier-search" value={supplierSearch} onChange={e => { setSupplierSearch(e.target.value); setSelectedSupplier(null); }}
            placeholder="Search supplier..." style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${selectedSupplier ? '#22c55e' : border}`, background: bgCard, color: text, fontSize: 12, boxSizing: 'border-box' }} />
          {!selectedSupplier && supplierSearch && (
            <div style={{ marginTop: 4, maxHeight: 140, overflowY: 'auto', border: `1px solid ${border}`, borderRadius: 8 }}>
              {filtered.map((s, i) => (
                <div key={i} onClick={() => { setSelectedSupplier(s); setSupplierSearch(s.SupplierName); }} style={{ padding: '6px 10px', fontSize: 11, cursor: 'pointer', color: text, borderTop: i > 0 ? `1px solid ${border}` : 'none' }}>{s.SupplierName} <span style={{ color: textMut }}>({s.SupplierId})</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
      {isGRN && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>PO NO. (OPTIONAL — leave blank for a GRN without a matching PO)</div>
          <input data-testid="staging-grn-pono" value={poNo} onChange={e => setPoNo(e.target.value)} placeholder="e.g. PO000012"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, background: bgCard, color: text, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>ITEM MASTER MAPPING</div>
      <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '7px 12px', background: isDark ? '#1a1a1a' : '#f8fafc', borderBottom: `1px solid ${border}` }}>
          {gridCols.map(c => <div key={c.key} style={{ flex: c.flex, fontSize: 9, fontWeight: 800, color: textMut, textTransform: 'uppercase' }}>{c.label}</div>)}
        </div>
        {lineItems.map((li, i) => (
          <div key={i} style={{ padding: '9px 12px', borderTop: i > 0 ? `1px solid ${border}` : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
              {gridCols.map(c => (
                <div key={c.key} style={{ flex: c.flex, fontSize: 11, color: c.key === 'description' ? text : textMut, fontFamily: c.key === 'description' || c.key === 'hsn' || c.key === 'uom' ? 'inherit' : 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: c.key === 'amount' ? 700 : 400 }}>
                  {c.key === 'amount' ? money(li.amount) : c.key === 'rate' ? (li.rate ? money(li.rate) : '—') : c.key === 'gstPct' ? (li.gstPct ? `${li.gstPct}%` : '—') : (li[c.key] || '—')}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
              <Package size={11} color={resolved[i] ? '#22c55e' : textMut} />
              <input data-testid={`staging-po-item-search-${i}`} value={itemSearch[i]} onChange={e => search(i, e.target.value)} placeholder="Search Item Master..."
                style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: `1px solid ${resolved[i] ? '#22c55e' : border}`, background: bgCard, color: text, fontSize: 11 }} />
              {resolved[i] && <CheckCircle2 size={13} color="#22c55e" />}
              {!resolved[i] && itemResults[i]?.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 20, right: 0, zIndex: 5, marginTop: 4, maxHeight: 140, overflowY: 'auto', border: `1px solid ${border}`, borderRadius: 8, background: bgCard, boxShadow: '0 4px 14px rgba(0,0,0,.2)' }}>
                  {itemResults[i].map((it, oi) => (
                    <div key={oi} data-testid={`staging-po-item-option-${i}-${oi}`} onClick={() => { setResolved(r => { const a = [...r]; a[i] = it; return a; }); setItemSearch(s => { const a = [...s]; a[i] = it.ItemDesc; return a; }); }}
                      style={{ padding: '6px 10px', fontSize: 11, cursor: 'pointer', color: text, borderTop: oi > 0 ? `1px solid ${border}` : 'none' }}>{it.ItemDesc} <span style={{ color: textMut }}>({it.Itemcode})</span></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {lineItems.length === 0 && <div style={{ padding: 16, fontSize: 11, color: textMut, textAlign: 'center' }}>No line items extracted.</div>}
      </div>
    </div>
  );
}

function ConfirmPanel({ detail, isPO, isGRN, isInvoice, isKyc, isJournal, poState, invoiceMapping, kycMapping, journalMapping, isDark, border, bgCard, text, textMut, onBack, onSubmit, posting }) {
  let summary = [];
  if (isPO || isGRN) {
    const total = (poState?.items || []).reduce((s, r) => s + (r.amount || 0), 0);
    summary = [`Supplier: ${poState?.partyName || '—'}`, `${poState?.items?.length || 0} line item(s) · Total: ${money(total)}`];
    if (isGRN && poState?.poNo) summary.push(`Against PO: ${poState.poNo}`);
  } else if (isInvoice) {
    summary = invoiceMapping.summary || [];
  } else if (isKyc) {
    summary = kycMapping.summary || [];
  } else if (isJournal) {
    summary = journalMapping.summary || [];
  }
  return (
    <div>
      <div data-testid="staging-confirm-summary" style={{ padding: 16, borderRadius: 10, border: `1px solid ${border}`, background: bgCard, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: text, marginBottom: 10 }}>Ready to commit: {detail.DocType.replace(/_/g, ' ')}</div>
        <div style={{ fontSize: 11, color: textMut, marginBottom: 4 }}>File: {detail.FileName}</div>
        {summary.map((line, i) => (
          <div key={i} style={{ fontSize: 12, color: text, padding: '4px 0', borderTop: i === 0 ? `1px solid ${border}` : 'none', marginTop: i === 0 ? 8 : 0 }}>{line}</div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button data-testid="staging-confirm-back-btn" onClick={onBack} disabled={posting} style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${border}`, background: 'transparent', color: text, fontSize: 12, cursor: 'pointer' }}>Back</button>
        <button data-testid="staging-confirm-submit-btn" onClick={onSubmit} disabled={posting}
          style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: '#22c55e', color: '#fff', fontSize: 13, fontWeight: 700, cursor: posting ? 'default' : 'pointer', opacity: posting ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {posting ? <><Loader2 size={14} className="animate-spin" /> Writing record to database…</> : 'Confirm & Send to DB'}
        </button>
      </div>
    </div>
  );
}

export default function StagingDetail({ id, isDark, border, bgCard, text, textMut, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0 review, 1 confirm, 2 done
  const [poState, setPoState] = useState(null);
  const [invoiceMapping, setInvoiceMapping] = useState({});
  const [kycMapping, setKycMapping] = useState({});
  const [journalMapping, setJournalMapping] = useState({});
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [fileBlobUrl, setFileBlobUrl] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/document-hub/staging/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => setDetail(d.data)).finally(() => setLoading(false));
  }, [id]);

  // Fetch file as blob using auth token — avoids iframe auth issues
  useEffect(() => {
    if (!detail) return;
    const url = `${API}/api/document-hub/staging/${id}/file`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => {
        if (!blob) return;
        const objUrl = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = objUrl;
        setFileBlobUrl(objUrl);
      })
      .catch(() => {});
    return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
  }, [detail, id]);

  const isPO = detail?.DocType === 'purchase_order';
  const isGRN = detail?.DocType === 'grn';
  const isInvoice = INVOICE_TYPES.includes(detail?.DocType);
  const isSalesReturn = detail?.DocType === 'sales_return';
  const isKyc = KYC_TYPES.includes(detail?.DocType);
  const isJournal = detail?.DocType === 'journal_voucher';
  const isSupported = isPO || isGRN || isInvoice || isKyc || isJournal;
  const isImage = /image\//.test(detail?.MimeType || '');
  const isPdf = detail?.MimeType === 'application/pdf';
  const canContinue = (isPO || isGRN) ? !!poState?.valid : isInvoice ? !!invoiceMapping.__valid : isKyc ? !!kycMapping.__valid : isJournal ? !!journalMapping.__valid : false;

  const handlePost = useCallback(async () => {
    if (!detail) return;
    setError(''); setPosting(true);
    try {
      let putBody;
      if (isPO || isGRN) {
        putBody = { partyId: poState.partyId, partyName: poState.partyName, extracted: { ...detail.extracted, lineItems: poState.items, poNo: isGRN ? poState.poNo : detail.extracted.poNo } };
      } else if (isInvoice) {
        const partyId = invoiceMapping.savePayload.VendorId || invoiceMapping.savePayload.ACId || '';
        putBody = { partyId, partyName: detail.extracted.partyName, extracted: { ...detail.extracted, _resolvedPayload: invoiceMapping.savePayload } };
      } else if (isKyc) {
        putBody = { partyId: kycMapping.savePayload.entityId, partyName: detail.extracted.name || detail.extracted.legalName || '', extracted: { ...detail.extracted, _resolvedPayload: kycMapping.savePayload } };
      } else if (isJournal) {
        putBody = { partyId: 'GL', partyName: 'Journal / GL Voucher', extracted: { ...detail.extracted, _resolvedPayload: journalMapping.savePayload } };
      } else {
        throw new Error('Posting is not wired up for this document type yet');
      }
      await fetch(`${API}/api/document-hub/staging/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(putBody) });
      const r = await fetch(`${API}/api/document-hub/staging/${id}/post`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` } });
      const d = await r.json();
      if (!d.success) throw new Error(d.error);
      setResult(d.refNo);
      setStep(2);
    } catch (e) {
      setError(e.message);
      setStep(1);
    } finally {
      setPosting(false);
    }
  }, [detail, isPO, isGRN, isInvoice, isKyc, isJournal, poState, invoiceMapping, kycMapping, journalMapping, id]);

  const handleReject = async () => {
    await fetch(`${API}/api/document-hub/staging/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
    onBack();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: textMut }}><Loader2 className="animate-spin" size={20} /></div>;
  if (!detail) return <div style={{ padding: 20, color: textMut }}>Not found.</div>;

  return (
    <div>
      <button data-testid="staging-detail-back-btn" onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: textMut, fontSize: 12, cursor: 'pointer', marginBottom: 14 }}>
        <ArrowLeft size={14} /> Back to Pending Imports
      </button>

      {isSupported && detail.Status === 'Pending' && <Stepper step={step} border={border} text={text} textMut={textMut} />}

      {step === 2 && result ? (
        <div data-testid="staging-post-success" style={{ padding: 24, borderRadius: 10, border: '1px solid #22c55e', background: 'rgba(34,197,94,0.08)', textAlign: 'center' }}>
          <CheckCircle2 size={32} color="#22c55e" style={{ marginBottom: 10 }} />
          <div style={{ fontSize: 15, fontWeight: 800, color: text }}>Posted successfully</div>
          <div style={{ fontSize: 12, color: textMut, marginTop: 6 }}>Reference: <span style={{ color: '#22c55e', fontFamily: 'monospace', fontWeight: 700 }}>{result}</span></div>
          <div style={{ fontSize: 11, color: textMut, marginTop: 4 }}>{detail.DocType.replace(/_/g, ' ')} · {new Date().toLocaleString()}</div>
          <button data-testid="staging-done-back-btn" onClick={onBack} style={{ marginTop: 18, padding: '10px 22px', borderRadius: 10, border: 'none', background: '#8b5cf6', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            Back to Pending Imports
          </button>
        </div>
      ) : step === 1 ? (
        <ConfirmPanel detail={detail} isPO={isPO} isGRN={isGRN} isInvoice={isInvoice} isKyc={isKyc} isJournal={isJournal} poState={poState} invoiceMapping={invoiceMapping} kycMapping={kycMapping} journalMapping={journalMapping}
          isDark={isDark} border={border} bgCard={bgCard} text={text} textMut={textMut}
          onBack={() => setStep(0)} onSubmit={handlePost} posting={posting} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>ORIGINAL FILE — {detail.FileName}</div>
            <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden', height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDark ? '#0f0f0f' : '#f1f1f1' }}>
              {fileBlobUrl && isImage && <img data-testid="staging-file-preview-img" src={fileBlobUrl} alt={detail.FileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
              {fileBlobUrl && isPdf && <iframe data-testid="staging-file-preview-pdf" title="preview" src={fileBlobUrl} style={{ width: '100%', height: '100%', border: 'none' }} />}
              {!fileBlobUrl && (isImage || isPdf) && <Loader2 size={18} color={textMut} className="animate-spin" />}
              {!isImage && !isPdf && fileBlobUrl && (
                <a href={fileBlobUrl} download={detail.FileName} style={{ color: '#8b5cf6', fontSize: 12 }}>Download {detail.FileName}</a>
              )}
              {!isImage && !isPdf && !fileBlobUrl && (
                <div style={{ fontSize: 11, color: textMut }}>No file preview available.</div>
              )}
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: textMut }}>{detail.DocType.replace(/_/g, ' ').toUpperCase()} · VALIDATE &amp; MATCH</div>
              {detail.extracted?._geminiUsed && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#8b5cf6', padding: '2px 8px', borderRadius: 10, background: '#8b5cf615' }}>
                  <Sparkles size={10} /><span>AI-enhanced extraction</span>
                </div>
              )}
            </div>
            <div style={{ maxHeight: 480, overflowY: 'auto', paddingRight: 4 }}>
              {(isPO || isGRN) && <POItemMapping extracted={detail.extracted} onChange={setPoState} isDark={isDark} border={border} bgCard={bgCard} text={text} textMut={textMut} isGRN={isGRN} />}
              {isInvoice && <InvoiceMapping extracted={detail.extracted} mapping={invoiceMapping} setMapping={setInvoiceMapping} isDark={isDark} border={border} bgCard={bgCard} text={text} textMut={textMut} isSales={detail.DocType === 'sales_invoice'} isSalesReturn={isSalesReturn} />}
              {isKyc && <KycMapping extracted={detail.extracted} mapping={kycMapping} setMapping={setKycMapping} isDark={isDark} border={border} bgCard={bgCard} text={text} textMut={textMut} category={detail.DocType} />}
              {isJournal && <JournalMapping extracted={detail.extracted} mapping={journalMapping} setMapping={setJournalMapping} isDark={isDark} border={border} bgCard={bgCard} text={text} textMut={textMut} />}
              {!isSupported && (
                <div>
                  <div style={{ padding: 14, borderRadius: 8, border: `1px dashed ${border}`, display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14 }}>
                    <AlertTriangle size={16} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 11, color: textMut }}>Posting for "{detail.DocType.replace(/_/g, ' ')}" isn't wired up yet — this import stays in Pending until that module is built. The extracted data is shown below for reference.</div>
                  </div>
                  <LineItemsGrid lineItems={detail.extracted.lineItems} taxLines={detail.extracted.taxLines} border={border} isDark={isDark} text={text} textMut={textMut} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && <div data-testid="staging-post-error" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', color: '#ef4444', fontSize: 12 }}>{error}</div>}

      {step === 0 && detail.Status === 'Pending' && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button data-testid="staging-reject-btn" onClick={handleReject} style={{ padding: '10px 18px', borderRadius: 10, border: `1px solid ${border}`, background: 'transparent', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>Reject</button>
          <button data-testid="staging-continue-confirm-btn" onClick={() => setStep(1)} disabled={!canContinue}
            style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: canContinue ? '#8b5cf6' : '#555', color: '#fff', fontSize: 13, fontWeight: 700, cursor: canContinue ? 'pointer' : 'not-allowed' }}>
            Continue to Confirm
          </button>
        </div>
      )}
    </div>
  );
}
